import { useCallback, useEffect, useState } from 'react';
import { object } from 'prop-types';
import { useDispatch } from 'react-redux';
import dynamic from 'next/dynamic';
import { renderArea } from '../../../utils/helpers/formater';

import styles from '../../MarketIndex/MarketDetail/index.module.scss';
import SubSlider from '../../common/Slider/SubSlider';
import {
  setCurrentPlace,
  setSearchDetailView,
} from '../../../store/market/actions';
import { numberWithCommas } from '../../../utils/helpers/thousandsSeparator';
import {
  DEFAULT_CENTER_MAP,
  DEFAULT_ZOOM_MAP,
  largeIndent,
  SLIDER_SETTINGS,
  smallIndent,
} from '../../../utils/constants';
import MultipleItems from '../../common/Slider';
import DetailInfo from './DetailInfo';
import { mappedBrands } from '../../../utils';
import usePluralize from '../../../utils/hooks/usePluralize';
import { numberAfterPoint } from '../../../utils/helpers/numberAfterPoint';

const Map = dynamic(() => import('./../../common/Map/index'), {
  ssr: false,
});

const HouseDetail = ({ houseData }) => {
  const { houseDetail, currentHouse, listings, city, translateListing } =
    houseData;
  const dispatch = useDispatch();

  const pluralizeAndTranslate = usePluralize(
    listings?.numberOfItems,
    houseDetail?.objectClass,
    null,
  );
  const [coords, setCoords] = useState(DEFAULT_CENTER_MAP);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM_MAP);

  useEffect(() => {
    if (houseDetail?.lat && houseDetail?.lng) {
      setCoords({ lat: houseDetail?.lat, lng: houseDetail?.lng });
      setZoom(15);
    }
    const house = Object.keys(currentHouse).length
      ? currentHouse
      : { fullTitle: '', urlSlug: 'berlin' };
    dispatch(setCurrentPlace(house));
    dispatch(
      setSearchDetailView({
        buyOrRent: houseDetail?.marketingType === 'rent' ? 'Mieten' : 'Kaufen',
        housing: houseDetail?.objectClass === 'house' ? 'haus' : 'wohnung',
        title: currentHouse?.fullTitle ? currentHouse.fullTitle : '',
      }),
    );
  }, [currentHouse, dispatch, houseDetail]);

  const translateDetailInfo = useCallback((value, obj) => {
    if (value === undefined) {
      return '';
    }
    if (obj[value]) {
      return obj[value] || '-';
    }
    return '';
  }, []);

  const redirect = useCallback((path) => window.open(path), []);

  const renderPrice = useCallback((data) => {
    if (data?.price) {
      return numberAfterPoint(data.price) === 0
        ? `${numberWithCommas(data.price)},00 €`
        : `${numberWithCommas(data.price.toFixed(2))} €`;
    }
    return '0 €';
  }, []);

  return (
    <div
      className="container"
      style={{ paddingBottom: `${smallIndent + 4.6875}rem` }}
    >
      <div>
        <h1
          style={{
            paddingBottom: `${smallIndent - 0.625}rem`,
            paddingTop: `${largeIndent}rem`,
          }}
          className="g-font-weight-700 text-left text-uppercase g-color-gray-dark-v3 mb-0"
        >
          {`${translateDetailInfo(
            houseDetail?.objectClass?.toUpperCase(),
            translateListing?.object_class,
          )} in `}
          <span className={styles.color}>{`${
            currentHouse?.fullTitle ? currentHouse.fullTitle : ''
          }`}</span>
        </h1>
        <div
          className={`'row no-gutters ' ${styles.marketBlock} ${styles.detailHouseContainer}`}
        >
          <div
            className={`g-mb-50 g-mb-0--lg col-lg-9 ${styles.detailHouseView}`}
          >
            <SubSlider isHouseDetail images={houseDetail?.images || []} />
          </div>
          <div
            className={`col-lg-3 g-mb-50 g-mb-0--lg ${styles.iframeBlockDetailHouse}`}
          >
            <Map
              center={coords}
              height="250px"
              zoom={zoom}
              selectedLayer="Wohnlage"
            />
            <div className={styles.houseDetailInfo}>
              <div>
                <span>
                  {`${houseDetail?.zipCode ? houseDetail.zipCode : ''}, 
                  ${currentHouse?.fullTitle ? currentHouse.fullTitle : ''}`}
                </span>
                <span>{`${
                  houseDetail?.numberOfRooms ? houseDetail.numberOfRooms : ''
                } Zimmer`}</span>
                <span className="font-weight-bold">
                  {' '}
                  {renderArea(
                    houseDetail?.livingSpace ? houseDetail.livingSpace : '',
                  )}{' '}
                </span>
                <span className="font-weight-bold">
                  {renderPrice(houseDetail)}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div>
          <h3
            style={{ marginTop: `${largeIndent - 0.375}rem` }}
            className="h2 g-color-gray-dark-v3 mb-0"
          >
            {houseDetail?.title}
          </h3>
        </div>
        <div
          className="d-flex row justify-content-around"
          style={{ marginTop: `${smallIndent - 1.4375}rem` }}
        >
          <DetailInfo
            currentHouse={currentHouse}
            translateDetailInfo={translateDetailInfo}
            houseDetail={houseDetail}
            translateListing={translateListing}
          />
        </div>
        <div style={{ marginTop: `${largeIndent - 0.3125}rem` }}>
          <div className="row d-flex justify-content-between">
            <div className="col-sm d-flex align-items-start flex-column">
              {houseDetail?.urls && (
                <h3 className="h3 g-mb-20 g-color-gray-dark-v3 text-left text-uppercase font-weight-bold">
                  Angebot aufrufen:
                </h3>
              )}
              <div className="d-flex row justify-content-between align-items-center">
                {mappedBrands(houseDetail?.urls).map((item) => (
                  <div key={Math.random()}>
                    {!!Object.keys(item).length && (
                      <div
                        className="col-lg-6 mx-auto d-flex flex-column align-items-center"
                        style={{ margin: '0 auto' }}
                      >
                        <div
                          style={{ height: '54px', width: '160px' }}
                          className="d-flex align-items-center justify-content-center"
                        >
                          <img
                            role="presentation"
                            src={item.src}
                            alt={item.alt}
                            className={styles.brandImage}
                            onClick={() => redirect(item.path)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: `${largeIndent}rem` }}>
          {listings?.items?.length ? (
            <>
              <h3
                className="h3 g-color-gray-dark-v3 text-left text-uppercase font-weight-bold"
                style={{ marginBottom: `${smallIndent}rem` }}
              >
                {`ähnliche ${pluralizeAndTranslate?.housing}`} in{' '}
                <span className={styles.color}>{city?.title}</span>
              </h3>
              <MultipleItems
                isShowIndicators
                title={city?.title}
                data={listings.items}
                horizontal
                settings={SLIDER_SETTINGS}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

HouseDetail.propTypes = {
  houseData: object.isRequired,
};

export default HouseDetail;
